import { Card, Col, Divider, Row, Tag, Tooltip } from 'antd'
import {
    WarningOutlined,
    ExclamationCircleOutlined,
    ClockCircleOutlined,
    RiseOutlined
} from "@ant-design/icons";

import React, { useEffect, useState } from 'react'
import { DEPARTMENT_RISK_HEATMAP } from '../../apis/apis';
import CulsightPageLoader from '../../components/CulsightPageLoader';

function DepartmentHeatmap() {

    const [Loader, setLoader] = useState(true);
    const [total_departments, set_total_departments] = useState(0);
    const [high_risk_departments, set_high_risk_departments] = useState(0);
    const [low_risk_departments, set_low_risk_departments] = useState(0);
    const [total_employees, set_total_employees] = useState(0);
    const [total_data, set_table_data] = useState([]);

    // ✅ Backend-based risk colors
    const RISK_COLORS = {
        "Low": "#13c2c2",
        "Low-Medium": "#22c55e",
        "Medium": "#f59e0b",
        "Medium-High": "#f97316",
        "High": "#ef4444"
    };

    const getColor = (risk_level) => {
        return RISK_COLORS[risk_level] || "#999"; // fallback
    };

    const DEPARTMENT_RISK_HEATMAP_LIST = async () => {
        const FORM_DATA = new FormData();
        const API_CALL = await DEPARTMENT_RISK_HEATMAP(FORM_DATA);

        if (API_CALL?.data?.status) {
            set_table_data(API_CALL?.data?.data);
            set_total_departments(API_CALL?.data?.total_departments);
            set_high_risk_departments(API_CALL?.data?.high_risk_departments);
            set_low_risk_departments(API_CALL?.data?.low_risk_departments);
            set_total_employees(API_CALL?.data?.total_employees);
        } else {
            console.log("error");
        }
        setLoader(false);
    };

    useEffect(() => {
        DEPARTMENT_RISK_HEATMAP_LIST();
    }, []);

   // ✅ Loading State
    if (Loader) {
        return (
            <div style={{ textAlign: "center", marginTop: 100 }}>
                <CulsightPageLoader />
            </div>
        );
    }

    return (
        <div className='lms-body'>
            <h1>Department Risk Heatmap</h1>
            <span style={{ color: "gray", fontSize: "14px", fontWeight: "bold" }}>
                Visualize department risk by sensitivity and phishing fail rate
            </span>

            <Card style={{ marginTop: "10px" }}>

                {/* TOP CARDS */}
                <Row gutter={[20, 20]}>
                    {[{
                        value: total_departments,
                        label: "Departments",
                        icon: <WarningOutlined style={{ color: "#8c3add", fontSize: 18 }} />,
                        bg: "#ede9fe"
                    },
                    {
                        value: high_risk_departments,
                        label: "High Risk",
                        icon: <ExclamationCircleOutlined style={{ color: "#ff4d4f", fontSize: 18 }} />,
                        bg: "#fee2e2"
                    },
                    {
                        value: low_risk_departments,
                        label: "Low Risk",
                        icon: <RiseOutlined style={{ color: "#52c41a", fontSize: 18 }} />,
                        bg: "#f6ffed"
                    },
                    {
                        value: total_employees,
                        label: "Total User",
                        icon: <ClockCircleOutlined style={{ color: "#1890ff", fontSize: 18 }} />,
                        bg: "#e6f4ff"
                    }].map((item, i) => (
                        <Col xs={24} sm={12} md={12} lg={6} key={i}>
                            <Card
                                hoverable
                                style={{ borderRadius: 16, height: 80, display: "flex", alignItems: "center" }}
                                bodyStyle={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}
                            >
                                <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                                    <div style={{ background: item.bg, padding: "10px", borderRadius: "12px" }}>
                                        {item.icon}
                                    </div>
                                    <div>
                                        <h2 style={{ margin: 0 }}>{item.value}</h2>
                                        <span style={{ color: "gray", fontSize: "13px" }}>{item.label}</span>
                                    </div>
                                </div>
                            </Card>
                        </Col>
                    ))}
                </Row>

                <Row gutter={20} style={{ marginTop: "30px" }}>

                    {/* HEATMAP */}
                    <Col xs={24} md={17}>
                        <Card title="Risk Matrix">

                            {(() => {
                                const getRiskBucket = (risk) => {
                                    if (risk < 20) return 4;
                                    if (risk < 40) return 3;
                                    if (risk < 60) return 2;
                                    if (risk < 80) return 1;
                                    return 0;
                                };

                                const getFailBucket = (fail) => {
                                    if (fail < 20) return 0;
                                    if (fail < 40) return 1;
                                    if (fail < 60) return 2;
                                    if (fail < 80) return 3;
                                    return 4;
                                };

                                const matrix = Array.from({ length: 5 }, () =>
                                    Array.from({ length: 5 }, () => [])
                                );

                                total_data.forEach(item => {
                                    const row = getRiskBucket(item.risk);
                                    const col = getFailBucket(item.fail_rate);
                                    matrix[row][col].push(item);
                                });

                                return (
                                    <>
                                        <span style={{ fontSize: "16px", color: "#666", fontWeight: "600" }}>
                                            Risk Rate
                                        </span>

                                        <div style={{ display: "flex", gap: "10px", overflowX: "auto" }}>


                                            <div style={{
                                                display: "grid",
                                                gridTemplateColumns: "repeat(5, 130px)",
                                                gridTemplateRows: "repeat(5, 100px)",
                                                gap: "16px",
                                                width: "100%",
                                                minWidth: "400px"
                                            }}>
                                                {matrix.map((row, rowIndex) =>
                                                    row.map((cell, colIndex) => {
                                                        const value = cell.length;

                                                        return (
                                                            <Tooltip
                                                                key={`${rowIndex}-${colIndex}`}
                                                                overlayInnerStyle={{
                                                                    backgroundColor: "#ffffff",
                                                                    color: "#000",
                                                                    padding: "10px",
                                                                    borderRadius: "8px",
                                                                    maxWidth: "250px",  
                                                                    maxHeight:"200px",
                                                                    overflowY: "auto"

                                                                }}
                                                                title={
                                                                    value
                                                                        ? cell.map((d, i) => (
                                                                            <div key={i}>
                                                                                <b>{d.department}</b><br />
                                                                                Risk: {d.risk}% , Fail: {d.fail_rate}%
                                                                                <hr />
                                                                            </div>
                                                                        ))
                                                                        : "No Data"
                                                                }
                                                            >
                                                                <Card
                                                                    hoverable
                                                                    style={{
                                                                        borderRadius: 10,
                                                                        background: value ? getColor(cell[0]?.risk_level) : "#f4f4f4",
                                                                        display: "flex",
                                                                        alignItems: "center",
                                                                        justifyContent: "center",
                                                                        color: "#fff",
                                                                        fontWeight: "bold",
                                                                        height: "100%",
                                                                        minHeight: "70px"
                                                                    }}
                                                                    bodyStyle={{
                                                                        padding: "8px"
                                                                    }}
                                                                >
                                                                    {value || ""}
                                                                </Card>
                                                            </Tooltip>
                                                        );
                                                    })
                                                )}
                                            </div>

                                        </div>
                                        <span style={{ fontSize: "16px", color: "#666", fontWeight: "600", marginLeft: "250px", marginTop: "10px" }}>Phishing Fail Rate (Low → High)</span>
                                        <Divider />

                                        {/* Legend */}
                                        <div style={{ display: "flex", justifyContent: "center", gap: "20px" }}>
                                            <span>Risk Level:</span>
                                            {Object.keys(RISK_COLORS).map(key => (
                                                <span key={key} style={{ color: RISK_COLORS[key] }}>
                                                    {key}
                                                </span>
                                            ))}
                                        </div>
                                    </>
                                );
                            })()}

                        </Card>
                    </Col>

                    {/* RIGHT LIST */}
                    <Col xs={24} md={7}>
                        <Card title="Departments">
                            <div style={{ maxHeight: "620px", overflowY: "auto" }}>
                                {total_data.map((item, index) => {
                                    const color = getColor(item.risk_level);

                                    return (
                                        <div key={index} style={{ marginBottom: "20px" }}>
                                            <div style={{ display: "flex", justifyContent: "space-between" }}>
                                                <div>
                                                    <h4>{item.department}</h4>
                                                    <span>{item.total_users} Users</span>
                                                </div>
                                                <div>
                                                    <h4 style={{ color, float: "right" }}>{item.risk}%</h4>
                                                    <br />
                                                    <span
                                                        style={{
                                                            background: "#fff7e6",
                                                            color: getColor(item.risk_level),
                                                            padding: "3px 10px",
                                                            borderRadius: "12px",
                                                            fontSize: "12px",
                                                        }}
                                                    >
                                                        {item.risk_level} Risk
                                                    </span>
                                                </div>
                                            </div>

                                            <div style={{ height: 6, background: "#eee", borderRadius: 10, marginTop: "8px" }}>
                                                <div style={{
                                                    width: `${item.risk}%`,
                                                    height: "100%",
                                                    background: color,
                                                    borderRadius: 10
                                                }} />
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </Card>
                    </Col>

                </Row>
            </Card>
        </div>
    )
}

export default DepartmentHeatmap;